/*
 * PDP size-selector redesign — Youth/Adult toggle + selection readout.
 * CRO dev-theme pass (2026-07). Spec: specs/sections/size-selector-redesign.md
 *
 * Progressive enhancement, vanilla JS, no theme-internal APIs:
 *  - Groups the mixed size run into Youth (Y-prefixed) / Adult (numeric) and
 *    shows one group at a time behind a segmented toggle.
 *  - Chips are hidden via a CSS class (kept in the DOM), so the theme's
 *    ProductForm / SelloutVariants (both keyed on `change` of [name^="options"])
 *    are never disturbed.
 *  - Selection persists across a toggle ONLY while it stays visible. A hidden chip
 *    must never remain the armed selection — see reconcile() for why (2026-07-31).
 *  - Renders ONLY when a size run actually contains both groups (so apparel /
 *    single-run sizes are left as a plain grid).
 *  - With JS off, all chips show — i.e. today's behavior. Safe fallback.
 */
(function () {
  'use strict';

  // Flip to 'adult' to match the redline's literal "Adult default on load".
  // Default 'selection' opens the group holding the pre-selected variant so the
  // selected chip is never hidden (Shopify pre-checks Y10 on these shoes).
  var INITIAL_GROUP_PREFERENCE = 'selection';

  var YOUTH_RE = /^y\s*\d/i; // "Y10", "Y1", "Y 3" -> youth

  function groupOf(label) {
    return YOUTH_RE.test(label.trim()) ? 'youth' : 'adult';
  }

  function chipLabel(btn) {
    var t = btn.querySelector('.option-title');
    return t ? t.textContent.trim() : '';
  }

  function enhance(fieldset) {
    if (!fieldset || fieldset.dataset.sizeEnhanced === '1') return;

    var buttons = Array.prototype.slice.call(
      fieldset.querySelectorAll('.radio__button')
    );
    if (buttons.length < 2) return;

    var hasYouth = false;
    var hasAdult = false;
    buttons.forEach(function (btn) {
      var g = groupOf(chipLabel(btn));
      btn.dataset.sizeGroup = g;
      if (g === 'youth') hasYouth = true;
      else hasAdult = true;
    });

    // Add the muted "current selection" readout to the SIZE label row regardless
    // (helps make the persisted selection visible across a toggle).
    var readout = addReadout(fieldset);
    updateReadout(fieldset, readout);
    fieldset.addEventListener('change', function () {
      updateReadout(fieldset, readout);
    });

    // Only build the toggle for genuinely mixed youth+adult runs (the shoes).
    if (!(hasYouth && hasAdult)) {
      fieldset.dataset.sizeEnhanced = '1';
      return;
    }

    // Pick mode (sell mode, 2026-10-08, spec specs/sections/sell-mode-2026-10-08.md):
    // set by Liquid as data-size-pick="adult". Opens on Adult (93.3% of Vengeance pairs
    // are adult) with NO size armed, and blocks every purchase path until the customer
    // picks a chip. Shopify still arms a hidden variant in the form's id input, so the
    // block below is what keeps the reconcile() invariant: no unseen size is ever bought.
    var pickMode = fieldset.dataset.sizePick === 'adult';

    var checked = fieldset.querySelector('.radio__input:checked');
    var checkedBtn = checked ? checked.closest('.radio__button') : null;
    var initial =
      !pickMode && INITIAL_GROUP_PREFERENCE === 'selection' && checkedBtn
        ? checkedBtn.dataset.sizeGroup
        : 'adult';

    if (pickMode) {
      fieldset.dataset.sizePicked = '0';
      buttons.forEach(function (btn) {
        var input = btn.querySelector('.radio__input');
        if (input) input.checked = false;
      });
      updateReadout(fieldset, readout);
      fieldset.addEventListener('change', function (e) {
        if (e.target && e.target.matches && e.target.matches('.radio__input') && e.target.checked) {
          fieldset.dataset.sizePicked = '1';
          fieldset.classList.remove('size-pick-needed');
        }
      });
      guardPurchase(fieldset);
    }

    var toggle = buildToggle(initial);
    var list = fieldset.querySelector('.radio__buttons');
    list.parentNode.insertBefore(toggle.el, list);

    // Remember the size this customer last chose in each group, so switching groups to
    // look around and coming back does not silently discard their pick. Records our own
    // programmatic selections too, which is intended.
    var lastChoice = {};
    fieldset.addEventListener('change', function () {
      var c = fieldset.querySelector('.radio__input:checked');
      var b = c ? c.closest('.radio__button') : null;
      if (b && b.dataset.sizeGroup) lastChoice[b.dataset.sizeGroup] = b;
    });

    // offsetParent is null for a chip hidden by ANY mechanism, not just ours — including
    // the camouflage-hide-sold-variants app embed, which hides variants at runtime and
    // knows nothing about size-group-hidden. Its hide settings are currently off, but it
    // is one config flip from hiding chips underneath us, so never auto-select something
    // that is not actually on screen. Guarded on the fieldset having a layout box because
    // on first paint nothing does, and every chip would otherwise read as hidden.
    function renderedHidden(btn) {
      return fieldset.offsetParent !== null && btn.offsetParent === null;
    }

    function isSelectable(btn) {
      if (btn.classList.contains('size-group-hidden')) return false;
      if (btn.classList.contains('sold-out')) return false;
      if (btn.classList.contains('unavailable')) return false;
      if (renderedHidden(btn)) return false;
      var input = btn.querySelector('.radio__input');
      return !!input && !input.disabled;
    }

    function selectChip(btn) {
      var input = btn.querySelector('.radio__input');
      if (!input || input.checked) return;
      input.checked = true;
      // ProductForm, SelloutVariants and the readout above all key on `change` of
      // [name^="options"], so dispatch on the input and let it bubble to all three.
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // The defect this exists to prevent: apply() used to change chip visibility and
    // nothing else, so a hidden chip stayed the armed selection. The customer saw the
    // Adult grid, the form still held Y10, and Add to Cart shipped a youth shoe with no
    // warning and no price difference. Measured on all 7 shoe PDPs, both directions,
    // 2026-07-31 — two taps, no size chip touched, /cart.js returned "Y10 / White".
    // Invariant: after any group switch the checked chip is in the displayed group and
    // is actually on screen.
    function reconcile(group) {
      var checked = fieldset.querySelector('.radio__input:checked');
      var checkedBtn = checked ? checked.closest('.radio__button') : null;
      if (checkedBtn && isSelectable(checkedBtn)) return;

      // Pick mode never chooses for the customer: a pick that is now hidden is cleared and
      // the purchase block re-arms, rather than swapping in a size they did not choose.
      if (pickMode) {
        if (checked) checked.checked = false;
        fieldset.dataset.sizePicked = '0';
        updateReadout(fieldset, readout);
        return;
      }

      var groupBtns = buttons.filter(function (btn) {
        return btn.dataset.sizeGroup === group;
      });

      var target = null;
      var remembered = lastChoice[group];
      if (remembered && groupBtns.indexOf(remembered) !== -1 && isSelectable(remembered)) {
        target = remembered;
      }
      if (!target) {
        groupBtns.some(function (btn) {
          if (isSelectable(btn)) target = btn;
          return !!target;
        });
      }
      // Whole group unpurchasable: fall back to the first chip that is at least visible,
      // so the armed variant still matches what is on screen. The theme's own sold-out
      // handling then blocks the purchase, which is honest. An armed hidden variant is not.
      if (!target) {
        groupBtns.some(function (btn) {
          if (!btn.classList.contains('size-group-hidden') && !renderedHidden(btn)) target = btn;
          return !!target;
        });
      }
      if (target) selectChip(target);
    }

    function apply(group) {
      buttons.forEach(function (btn) {
        btn.classList.toggle(
          'size-group-hidden',
          btn.dataset.sizeGroup !== group
        );
      });
      toggle.setActive(group);
      reconcile(group);
    }

    toggle.youthBtn.addEventListener('click', function () {
      apply('youth');
    });
    toggle.adultBtn.addEventListener('click', function () {
      apply('adult');
    });

    // Also covers INITIAL_GROUP_PREFERENCE = 'adult': that setting would otherwise arm a
    // hidden Y10 on first paint with zero interaction, which is the same bug, worse.
    apply(initial);
    fieldset.dataset.sizeEnhanced = '1';
  }

  // Blocks Add to Cart, the sticky bar (it clicks the real button), dynamic checkout
  // (Shop Pay) and form submit for this fieldset's form until a size is picked.
  function guardPurchase(fieldset) {
    var form = fieldset.closest('form');
    if (!form) return;
    var scope = form.closest('[data-product-single], .product-single, .product__wrapper, section') || form.parentNode;

    function blocked() {
      return fieldset.dataset.sizePicked !== '1';
    }
    function stop(e) {
      e.preventDefault();
      e.stopImmediatePropagation();
      fieldset.classList.add('size-pick-needed');
      var legend = fieldset.querySelector('.radio__legend') || fieldset;
      // scrollIntoView also scrolls overflow-hidden ancestors sideways (the page lost its
      // left gutter at 390), so scroll the window only.
      window.scrollTo({ top: legend.getBoundingClientRect().top + window.scrollY - 120, behavior: 'smooth' });
    }

    document.addEventListener('click', function (e) {
      if (!blocked()) return;
      var t = e.target && e.target.closest ? e.target : null;
      if (!t) return;
      var atc = t.closest('[data-add-to-cart]');
      var pay = t.closest('.shopify-payment-button, shopify-buy-it-now-button, shopify-accelerated-checkout');
      if ((atc && form.contains(atc)) || (pay && scope.contains(pay))) stop(e);
    }, true);

    form.addEventListener('submit', function (e) {
      if (blocked()) stop(e);
    }, true);
  }

  function buildToggle(active) {
    var el = document.createElement('div');
    el.className = 'size-group-toggle';
    el.setAttribute('role', 'group');
    el.setAttribute('aria-label', 'Youth or adult sizes');

    var youthBtn = mkBtn('youth', 'Youth');
    var adultBtn = mkBtn('adult', 'Adult');
    el.appendChild(youthBtn);
    el.appendChild(adultBtn);

    function setActive(group) {
      youthBtn.setAttribute('aria-pressed', String(group === 'youth'));
      adultBtn.setAttribute('aria-pressed', String(group === 'adult'));
    }
    setActive(active);
    return { el: el, youthBtn: youthBtn, adultBtn: adultBtn, setActive: setActive };
  }

  function mkBtn(group, text) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'size-group-toggle__btn';
    b.dataset.sizeGroupBtn = group;
    b.textContent = text;
    b.setAttribute('aria-pressed', 'false');
    return b;
  }

  function addReadout(fieldset) {
    var legend = fieldset.querySelector('.radio__legend');
    var span = document.createElement('span');
    span.className = 'size-selected-readout';
    span.setAttribute('aria-live', 'polite');
    if (legend) {
      var labelEl = legend.querySelector('.radio__legend__label');
      if (labelEl && labelEl.parentNode) {
        labelEl.parentNode.insertBefore(span, labelEl.nextSibling);
      } else {
        legend.appendChild(span);
      }
    }
    return span;
  }

  function updateReadout(fieldset, span) {
    if (!span) return;
    var checked = fieldset.querySelector('.radio__input:checked');
    var btn = checked ? checked.closest('.radio__button') : null;
    if (btn) span.textContent = chipLabel(btn);
    else span.textContent = fieldset.dataset.sizePick === 'adult' ? 'Pick your size' : '';
  }

  function init(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var sets = scope.querySelectorAll('.radio__fieldset--size');
    Array.prototype.forEach.call(sets, enhance);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  // Theme editor: re-init when a section is re-rendered.
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target || document);
  });

  // Pick-mode fieldsets that arrive after load: the quick view injects the product form
  // without running this file, which left Vengeance armed on Y10 there. Only fieldsets
  // carrying data-size-pick are touched, so every other quick view is unchanged.
  if (window.MutationObserver) {
    new MutationObserver(function (list) {
      list.forEach(function (m) {
        Array.prototype.forEach.call(m.addedNodes, function (n) {
          if (n.nodeType !== 1) return;
          if (n.matches('.radio__fieldset--size[data-size-pick]')) enhance(n);
          else Array.prototype.forEach.call(n.querySelectorAll('.radio__fieldset--size[data-size-pick]'), enhance);
        });
      });
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
